import { AnimatePresence, motion } from "framer-motion";
import { CircleHelp, History, Home, Menu, User, Wallet, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  FaEthereum,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaShoppingCart,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  LogoDark,
  LogoLight,
  NotificationBell,
} from "../../SVGComponents";
import GradientButton from "../../UIComponents/GradientButton";
import IconCard from "../../UIComponents/IconCard";
import ThemeToggle from "../ThemeToggle";
import UserMenu from "../UserMenu";
import NotificationDropdown from "../NotificationDropdown";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { getUserProfile } from "../../redux/slices/authSlice";

interface IconDataItem {
  id: number;
  Icon: React.ComponentType<{ count?: number }>;
  path?: string;
  count?: number;
}

const Header = (userToken: any) => {
  const location = useLocation();

  const menus = [
    { name: "Home", path: "/home", icon: Home },
    { name: "Spot", path: "/spot/BTC_USDC", icon: FaMapMarkerAlt },
    { name: "Market", path: "/market", icon: FaShoppingCart },
    { name: "Stake", path: "/stake", icon: FaEthereum },
    { name: "History", path: "/history", icon: History },
    { name: "P2P", path: "/p2p", icon: FaNetworkWired },
    { name: "Profile", path: "/profile", icon: User },
    { name: "Support", path: "/support", icon: CircleHelp },
  ];
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);

  // Circular reveal animation variants
  const sidebar = {
    open: (height = 1000) => ({
      clipPath: `circle(${height * 2 + 200}px at calc(100% - 40px) 40px)`,
      transition: {
        type: "spring",
        stiffness: 20,
        restDelta: 2,
      },
    }),
    closed: {
      clipPath: "circle(0px at calc(100% - 40px) 40px)",
      transition: {
        delay: 0.2,
        type: "spring",
        stiffness: 400,
        damping: 40,
      },
    },
  };

  // Menu items animation variants
  const drawerVariants = {
    open: {
      transition: { staggerChildren: 0.07, delayChildren: 0.3 },
    },
    closed: {
      transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
  };

  const itemVariants = {
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: { stiffness: 1000, velocity: -100 },
      },
    },
    closed: {
      y: 50,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  };

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const handleLogout = () => {
    localStorage.removeItem("crymadx");
    setTimeout(() => {
      window.location.href = "/login";
    }, 1000);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        setIsDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  let token = localStorage.getItem("crymadx");
  const notificationCount = 3; // Replace this with your dynamic count from state or props

  const iconData: IconDataItem[] = [
    { id: 1, Icon: ThemeToggle },
    ...(token
      ? [
        // {
        //   id: 2,
        //   Icon: NotificationDropdown,
        //   count: notificationCount,
        // } as IconDataItem,
      ]
      : []),
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <>
      <header className="absolute top-0 left-0 w-full bg-white/50 dark:bg-black/50 z-50 backdrop-blur-sm">
        <nav
          className="flex flex-row xs:flex-row xs-sm:flex-row sm:flex-row md:flex-row justify-between items-center w-full 
        px-4 xs:px-xs-px sm:px-sm-px md:px-[50px] lg:px-[50px] xl:px-[150px] 2xl:px-[250px] 3xl:px-[350px] 4xl:px-[550px] 5xl:px-[800px] 6xl:px-[950px] 7xl:px-[1000px] 8xl:px-[1250px] 9xl:px-[1250px] 10xl:px-[1350px] 11xl:px-[2100px] 12xl:px-[2900px]
         py-3 "
          aria-label="Global"
        >
          <div className="">
            <a href="/" className="">
              <div className="block dark:hidden">
                <LogoLight />
              </div>
              <div className="hidden dark:block">
                <LogoDark />
              </div>
            </a>
          </div>

          <div className="hidden lg:flex gap-5">
            {menus
              .filter(
                (item) => item.name !== "Profile" && item.name !== "Support"
              )
              .map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center gap-3 text-[15px] font-medium pb-1 ${(item.name === "Home" &&
                    ["/", "/home", ""].includes(location.pathname)) ||
                    location.pathname === item.path
                    ? "border-b-2 border-themeGreen dark:border-themeGreen text-themeColor"
                    : "hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600 text-black dark:text-white"
                    }`}
                >
                  {item.name}
                </Link>
              ))}
          </div>
          {token && (
            <div className="hidden lg:flex">
              <GradientButton
                text="Wallet"
                Icon={Wallet}
                onClick={() => navigate("/wallet")}
              />
            </div>
          )}

          <div className="flex gap-5 items-center">
            <div className="flex gap-4 flex-row-reverse items-center">

              <NotificationDropdown count={notificationCount} />
              <div className="block lg:hidden">
                <IconCard Icon={ThemeToggle} />

              </div>

            </div>
            <div className="hidden lg:flex gap-4 flex-row-reverse items-center">
              {iconData.map(({ id, Icon, path, count }) =>
                typeof path === "string" ? (
                  <Link to={path} key={id}>
                    <IconCard Icon={Icon} count={count} />
                  </Link>
                ) : (
                  <div key={id} className="pt-2">
                    <Icon count={count} />
                  </div>
                )
              )}

              {token ? (
                <div>
                  <UserMenu />
                </div>
              ) : (
                <div className="flex space-x-2">
                  <GradientButton
                    onClick={() => navigate("/login")}
                    text="Login"
                    className="px-6 py-[8px] text-center"
                  />
                  <GradientButton
                    onClick={() => navigate("/register")}
                    text="Register"
                    className="px-6 py-[8px] text-center"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-4 lg:hidden items-center">
              {!isDrawerOpen && (
                <button onClick={toggleDrawer}>
                  {/* {token ? (
                    <IconCard Icon={Avatar} />
                  ) : (
                    <Menu className="w-6 h-6 text-gray-700 dark:text-white" />
                  )} */}
                </button>
              )}
            </div>
          </div>
        </nav>

        <hr className="h-[1px] border-0 bg-gray-200 dark:bg-gray-700" />

        <AnimatePresence>
          {isDrawerOpen && (
            <div className="relative">
              {/* Overlay */}
              <div
                className="absolute top-0 left-0 w-full bg-white/50 dark:bg-black/50 z-50 backdrop-blur-sm"
                onClick={toggleDrawer}
              />

              {/* Drawer Container */}
              <motion.div
                className="h-screen block lg:hidden fixed top-0 right-0 w-full sm:w-[70%] z-50 shadow-lg shadow-gray-500 bg-white dark:bg-black"
                ref={drawerRef}
              >
                <button
                  onClick={toggleDrawer}
                  className="z-100 absolute -left-10 top-4 w-8 h-8 flex items-center justify-center rounded-full bg-white dark:bg-gray-600 shadow-md"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-red-600" />
                </button>
                {/* Animated Background */}
                <motion.div
                  className="absolute inset-0 bg-white dark:bg-black"
                  initial="closed"
                  animate="open"
                  exit="closed"
                  variants={sidebar}
                />

                {/* Content Layer */}
                <div className="absolute top-0 left-0 w-full bg-white/50 dark:bg-black/50 z-50 backdrop-blur-xl">
                  {/* Header */}
                  <div className="px-5 py-[14px] border-b dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-lg font-bold dark:text-white">
                          User Menu
                        </h2>
                      </div>
                      <div className="flex gap-6">
                        {/* <IconCard Icon={ThemeToggle} /> */}
                        <button
                          onClick={toggleDrawer}
                          className="sm:hidden bg-gray-600 rounded-full w-6 h-6 flex items-center justify-center"
                        >
                          <X className="w-4 h-4 text-white dark:text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Scrollable Menu Content */}
                  <motion.div
                    className="flex-1 overflow-y-auto h-[90vh] p-5 scrollbar-thin "
                    variants={drawerVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                  >
                    {/* Menu Items */}
                    {menus.map((item) => {
                      const isActive =
                        (item.path === "/home" && (location.pathname === "/" || location.pathname === "/home")) ||
                        location.pathname === item.path;

                      return (
                        <motion.div key={item.name} variants={itemVariants} className="mb-3" onClick={() => scrollToTop()}>
                          <Link
                            to={item.path}

                            className={`flex items-center gap-3 text-sm font-medium py-2 px-3 rounded-lg ${isActive
                              ? "bg-themeGreen text-white"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              }`}
                            onClick={toggleDrawer}
                          >
                            <item.icon className="w-5 h-5" />
                            {item.name}
                          </Link>
                        </motion.div>
                      );
                    })}


                    {/* Additional Content */}
                    {/* <motion.div variants={itemVariants} className="mt-6">
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                        <h3 className="font-bold text-sm dark:text-white mb-2">
                          Top Cryptos in market:
                        </h3>
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          <div className="grid grid-cols-4 gap-2 font-medium mb-1">
                            <span>Name</span>
                            <span>Last Price</span>
                            <span>24h Change</span>
                            <span>Market Cap</span>
                          </div>
                        </div>
                      </div>
                    </motion.div> */}

                    {/* Auth Buttons */}
                    {token ? (
                      <div className="mt-6 space-y-3">
                        <motion.div variants={itemVariants}>
                          <GradientButton
                            onClick={() => { navigate("/wallet"); setIsDrawerOpen(false); scrollToTop() }}
                            text="Wallet"
                            className="w-full"
                          />
                        </motion.div>
                        <motion.div variants={itemVariants}>
                          <button
                            onClick={handleLogout}
                            className="w-full py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white"
                          >
                            Logout
                          </button>
                        </motion.div>
                      </div>
                    ) : (
                      <motion.div
                        variants={itemVariants}
                        className="mt-6 grid grid-cols-2 gap-3"
                      >
                        <GradientButton
                          onClick={() => navigate("/login")}
                          text="Login"
                        />
                        <GradientButton
                          onClick={() => navigate("/register")}
                          text="Register"
                        />
                      </motion.div>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
        {!isDrawerOpen && (
          <div className="fixed flex top-4 right-2 md:right-4  lg:hidden mr-0 md:mr-4 lg:mr-2">
            {/* <NotificationBell/>
           <NotificationDropdown count="4"/> */}
            <button onClick={toggleDrawer}>
              <Menu className="w-6 h-6 text-green-700 dark:text-white" />
            </button>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
